import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../../../../api';
import { useLogos } from '../../../../../context/LogoProvider';

// 🔹 Configuración dinámica de URLs basada en API_BASE
const SERVER_URL = API_BASE.replace(/\/api\/?$/, ''); // Elimina /api al final
const UPLOAD_ENDPOINT = `${API_BASE}/upload/firma-imagen`;

const EMPTY = {
  idEmpleado: '',
  firmaUrl: '',
  fechaRegistro: '',
  fechaVigencia: '',
  esDirector: false,
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  empleados = [],
  personas = [],
  onClose,
  onSubmit,
  inline = false,
}) {
  const { getSystemConfig } = useLogos();
  const [form, setForm] = useState(EMPTY);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previousImagePath, setPreviousImagePath] = useState(null);
  const [errors, setErrors] = useState(EMPTY);

  const availableEmpleados = useMemo(() => {
    return empleados.map(e => {
      const p = personas.find(p => p.idPersona === e.idPersona);
      const nombre = p ? `${p.nombre} ${p.apellidoPaterno} ${p.apellidoMaterno || ''}`.trim() : `Empleado ${e.idEmpleado}`;
      return { idEmpleado: e.idEmpleado, nombre };
    }).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [empleados, personas]);

  const validateField = (field, value) => {
    let error = '';
    if (field === 'esDirector') return; // Boolean doesn't need validation
    if (!value) {
      error = 'Este campo es obligatorio.';
    }
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const validateAll = () => {
    const newErrors = {};
    let isValid = true;
    Object.keys(EMPTY).forEach(key => {
      if (key === 'esDirector') return;
      if (!form[key]) {
        newErrors[key] = 'Este campo es obligatorio.';
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar que sea PNG específicamente (para mantener transparencia)
    if (file.type !== 'image/png') {
      setErrors((prev) => ({ ...prev, firmaUrl: 'Solo se permiten archivos PNG para mantener la transparencia.' }));
      return;
    }

    const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
    if (file.size > maxMB * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, firmaUrl: `La imagen no debe superar los ${maxMB}MB.` }));
      return;
    }

    setSelectedFile(file);
    const blobUrl = URL.createObjectURL(file);
    setForm((prev) => ({ ...prev, firmaUrlPreview: blobUrl }));

    try {
      setIsUploading(true);
      if (previousImagePath) {
        try {
          await fetch(`${UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(previousImagePath)}`, { method: 'DELETE' });
        } catch (deleteError) {
          console.warn('No se pudo eliminar la imagen anterior:', deleteError);
        }
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(UPLOAD_ENDPOINT, { method: 'POST', body: formData });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al subir el archivo');
      }

      const data = await response.json();
      setPreviousImagePath(data.filePath);
      setForm((prev) => ({ ...prev, firmaUrl: data.filePath, firmaUrlPreview: blobUrl }));
      validateField('firmaUrl', data.filePath);
    } catch (error) {
      console.error('Error al subir el archivo:', error);
      setErrors((prev) => ({ ...prev, firmaUrl: error.message || 'Error al subir la imagen al servidor.' }));
      setForm((prev) => ({ ...prev, firmaUrlPreview: null }));
      setSelectedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const filledForm = {
        idFirma: data.idFirma ?? 0,
        idEmpleado: data.idEmpleado ?? '',
        firmaUrl: data.firmaUrl ?? '',
        fechaRegistro: data.fechaRegistro ?? '',
        fechaVigencia: data.fechaVigencia ?? '',
        esDirector: !!data.esDirector,
      };
      setForm(filledForm);

      // Guardar la ruta de la imagen actual para poder eliminarla si se actualiza
      if (filledForm.firmaUrl) {
        setPreviousImagePath(filledForm.firmaUrl);
      }

      // Validate pre-filled data
      Object.keys(filledForm).forEach(key => {
        if (key in EMPTY) validateField(key, filledForm[key]);
      });
    } else {
      setForm(EMPTY);
      setPreviousImagePath(null);
      setErrors(EMPTY);
    }
  }, [mode, data]);

  if (!open) return null;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva firma' : 'Editar firma'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Empleado: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <select
          className={`infoField${errors.idEmpleado ? ' infoField--error' : ''}`}
          value={form.idEmpleado}
          onChange={(e) => handleChange('idEmpleado', Number(e.target.value) || '')}
        >
          <option value="">-- Selecciona un empleado --</option>
          {availableEmpleados.map(e => (
            <option key={e.idEmpleado} value={e.idEmpleado}>{e.nombre}</option>
          ))}
        </select>
        {errors.idEmpleado && <small style={errorStyle}>{errors.idEmpleado}</small>}
      </div>

      {/* Imagen de Firma (PNG) */}
      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Imagen de Firma (PNG): <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>

          {/* Área de carga con preview integrado */}
          <div
            className={`file-uploader${form.firmaUrl ? ' file-uploader--filled' : ''}${errors.firmaUrl ? ' file-uploader--error' : ''}${isUploading ? ' file-uploader--uploading' : ''}`}
          >
            {form.firmaUrl && (
              <div className="file-uploader__badge">
                <span>✓</span>
                <span>Archivo cargado</span>
              </div>
            )}

            {form.firmaUrl && (
              <div className="file-uploader__preview">
                <img
                  src={form.firmaUrlPreview || (form.firmaUrl?.startsWith('http') ? form.firmaUrl : `${SERVER_URL}${form.firmaUrl}`)}
                  alt="Vista previa de la firma"
                  style={{ backgroundColor: '#f1f5f9' }}
                />
              </div>
            )}

            <label className="file-uploader__label">
              <input
                type="file"
                accept="image/png"
                onChange={handleFileChange}
                onClick={(e) => { e.target.value = ''; }}
                style={{ display: 'none' }}
              />
              <div className="file-uploader__icon">
                {form.firmaUrl ? '🔄' : '✍️'}
              </div>
              <div className="file-uploader__text">
                {form.firmaUrl ? 'Cambiar imagen' : 'Haz clic para subir firma'}
              </div>
              <div className="file-uploader__hint">
                Solo PNG (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB) - Mantiene transparencia
              </div>
            </label>
          </div>

          {errors.firmaUrl && (
            <small style={errorStyle}>{errors.firmaUrl}</small>
          )}
        </div>
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Fecha registro: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.fechaRegistro ? ' infoField--error' : ''}`}
          onKeyDown={(e) => e.preventDefault()}
          type="date"
          value={form.fechaRegistro ? String(form.fechaRegistro).substring(0, 10) : ''}
          onChange={(e) => handleChange('fechaRegistro', e.target.value)}
        />
        {errors.fechaRegistro && <small style={errorStyle}>{errors.fechaRegistro}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Fecha vigencia: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.fechaVigencia ? ' infoField--error' : ''}`}
          onKeyDown={(e) => e.preventDefault()}
          type="date"
          value={form.fechaVigencia ? String(form.fechaVigencia).substring(0, 10) : ''}
          onChange={(e) => handleChange('fechaVigencia', e.target.value)}
        />
        {errors.fechaVigencia && <small style={errorStyle}>{errors.fechaVigencia}</small>}
      </div>

      <div style={{
        gridColumn: '1 / 3',
        display: 'flex',
        alignItems: 'center',
        gap: '0.8rem',
        marginTop: '0.5rem'
      }}>
        <div
          onClick={() => handleChange('esDirector', !form.esDirector)}
          style={{
            width: '40px',
            height: '21px',
            backgroundColor: form.esDirector ? '#10b981' : '#cbd5e1',
            borderRadius: '12px',
            position: 'relative',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            flexShrink: 0,
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{
            width: '15px',
            height: '15px',
            backgroundColor: 'white',
            borderRadius: '50%',
            position: 'absolute',
            top: '3px',
            left: form.esDirector ? '22px' : '3px',
            transition: 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }} />
        </div>
        <label
          onClick={() => handleChange('esDirector', !form.esDirector)}
          className="roles-title"
          style={{ fontSize: '.95rem', margin: 0, cursor: 'pointer', userSelect: 'none' }}
        >
          Es director
        </label>
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (validateAll()) {
            onSubmit(form, mode);
          }
        }}
        disabled={isUploading || Object.values(form).some(x => x === '' || x === null) || Object.values(errors).some(x => x)}
        style={{
          opacity: isUploading || Object.values(form).some(x => x === '' || x === null) || Object.values(errors).some(x => x) ? 0.5 : 1,
          cursor: isUploading || Object.values(form).some(x => x === '' || x === null) || Object.values(errors).some(x => x) ? 'not-allowed' : 'pointer',
        }}
      >
        {isUploading ? 'Subiendo...' : (mode === 'new' ? 'Guardar' : 'Actualizar')}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
